import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Search, ChevronDown, Mail, MessageSquare, 
   Wifi, Globe 
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Helpandfaq = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [openId, setOpenId] = useState(null);

  const categories = ["All", "Sync", "Account", "Billing", "Data"];

  const faqs = [
    { 
      id: 1, 
      cat: "Sync", 
      q: "Does the app work offline?", 
      a: "No. To ensure your health data is always accurate and securely backed up, Fitness Tracker requires an active internet connection. Offline logging is currently not supported to prevent data conflicts." 
    },
    { 
      id: 2, 
      cat: "Data", 
      q: "How is my BMI calculated?", 
      a: "BMI is calculated in real-time on our servers using your weight (kg) divided by the square of your height (m). This requires a connection to update your dashboard." 
    },
    { 
      id: 3, 
      cat: "Account", 
      q: "Is my health data secure?", 
      a: "Yes. All data is encrypted and synced directly to Database. Because we don't store sensitive health metrics locally on the device, your data stays safe even if you lose your phone." 
    },
    { 
      id: 4, 
      cat: "Billing", 
      q: "What are Pro Plan benefits?", 
      a: "Pro Plan offers advanced AI coaching, priority cloud syncing, and detailed PDF progress reports generated directly from our server." 
    }
  ];

  const filteredFaqs = faqs.filter(f => 
    (activeCategory === "All" || f.cat === activeCategory) &&
    f.q.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900 selection:bg-emerald-100">
      <div className="max-w-4xl mx-auto p-6 md:p-12 lg:pt-20">
        
        {/* BACK BUTTON */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-emerald-600 font-black uppercase text-[10px] tracking-[0.3em] mb-12 hover:translate-x-[-4px] transition-transform"
        >
          <ArrowLeft size={16} /> Back to Account
        </button>

        {/* HERO SECTION */}
        <div className="mb-12">
          <h1 className="text-6xl font-black tracking-tighter italic mb-4">Help <span className="text-emerald-500">Center</span></h1>
          <div className="flex items-center gap-2 text-slate-400 font-bold text-[10px] uppercase tracking-widest mb-10">
            <Wifi size={14} className="text-emerald-500" /> Always Online • Real-time Syncing
          </div>

          {/* SEARCH BOX */}
          <div className="relative group">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-emerald-500 transition-colors" size={20} />
            <input 
              type="text" 
              placeholder="How can we help you today?"
              className="w-full bg-white border-2 border-slate-100 rounded-[32px] p-7 pl-16 font-bold text-slate-700 outline-none focus:border-emerald-500/20 transition-all shadow-xl shadow-slate-200/20"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* CATEGORY TABS */}
        <div className="flex gap-3 overflow-x-auto pb-8 no-scrollbar">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border-2 ${
                activeCategory === cat 
                ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                : "bg-white border-slate-50 text-slate-400 hover:border-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ ACCORDIONS */}
        <div className="space-y-4">
          <AnimatePresence mode="wait">
            {filteredFaqs.map((faq) => (
              <motion.div 
                key={faq.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-[32px] border-2 transition-all overflow-hidden ${
                  openId === faq.id ? "border-emerald-500/20 shadow-xl" : "border-slate-50 shadow-sm"
                }`}
              >
                <button 
                  onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                  className="w-full flex items-center justify-between p-8 text-left"
                >
                  <span className={`font-black tracking-tight text-lg ${openId === faq.id ? "text-emerald-600" : "text-slate-800"}`}>
                    {faq.q}
                  </span>
                  <ChevronDown size={20} className={`text-slate-300 transition-transform ${openId === faq.id ? "rotate-180 text-emerald-500" : ""}`} />
                </button>
                <AnimatePresence>
                  {openId === faq.id && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }} 
                      animate={{ height: "auto", opacity: 1 }} 
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <div className="px-8 pb-8 pt-2 text-slate-500 font-medium text-sm leading-relaxed border-t-2 border-slate-50 italic">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* ONLINE STATUS BANNER */}
        <div className="mt-16 bg-slate-900 rounded-[40px] p-10 text-white relative overflow-hidden group">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                <Globe size={12} /> Live Cloud Sync
              </div>
              <h3 className="text-3xl font-black mb-2 italic">Stay Connected</h3>
              <p className="text-slate-400 font-medium text-sm max-w-sm leading-relaxed">
               Fitness Tracker uses live database syncing to keep your metrics safe. Ensure you have a stable Wi-Fi or Cellular connection.
              </p>
            </div>
            
            <div className="flex gap-3">
              <a href="mailto:support@fitnesstracker.in" className="bg-white text-slate-900 p-5 rounded-3xl hover:bg-emerald-500 hover:text-white transition-all shadow-xl">
                <Mail size={24} />
              </a>
            
            </div>
          </div>
          
          {/* DECOR */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[100px] -mr-32 -mt-32" />
        </div>
      </div>
    </div>
  );
};

export default Helpandfaq;